import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import type { CustomContentProps } from 'notistack';
import { SnackbarContent } from 'notistack';
import React, { useCallback } from 'react';

import { ConvoV2CloseIcon } from '../convoV2/ConvoV2CloseIcon';
import { ConvoV2NotificationContent } from '../convoV2/ConvoV2NotificationContent';

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  clickableWrapper: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: 'inherit',
    '&:hover': {
      opacity: 0.9,
      transform: 'scale(1.02)',
    },
  },
  button: {
    padding: 0,
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  arrowIcon: {
    fontSize: '16px !important',
    opacity: 0.7,
  },
}));

export type ConvoV2NotificationProps = {
  icon?: React.ReactNode;
  isDesktop: boolean | undefined;
  subtitle?: string;
  onClick?: () => void;
};
type Props = CustomContentProps & ConvoV2NotificationProps;

const ConvoV2Notification = React.forwardRef<HTMLDivElement, Props>(
  ({ icon, message, subtitle, closeSnackbar, id, isDesktop, onClick }, ref) => {
    const classes = useStyles();
    const theme = useTheme();

    const handleCloseClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (closeSnackbar && id) {
          return closeSnackbar(id as string);
        }
      },
      [closeSnackbar, id]
    );

    const handleNotificationClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onClick) {
          onClick();
          if (closeSnackbar && id) {
            closeSnackbar(id as string);
          }
        }
      },
      [onClick, closeSnackbar, id]
    );

    const messageContent = subtitle ? (
      <Box className={classes.messageContainer}>
        <Typography
          component="span"
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
          }}
        >
          {message}
        </Typography>
        <Typography
          component="span"
          style={{
            fontSize: '0.875em',
            opacity: 0.8,
            color: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    ) : (
      message
    );

    return (
      <SnackbarContent className={classes.wrapper} ref={ref}>
        <Box
          className={onClick ? classes.clickableWrapper : undefined}
          onClick={onClick ? handleNotificationClick : undefined}
          style={{
            width: '100%',
          }}
        >
          <ConvoV2NotificationContent desktop={!!isDesktop}>
            {icon}
            {messageContent}
            {onClick && <KeyboardArrowDownIcon className={classes.arrowIcon} />}
            <IconButton className={classes.button} onClick={handleCloseClick}>
              <ConvoV2CloseIcon desktop={!!isDesktop} />
            </IconButton>
          </ConvoV2NotificationContent>
        </Box>
      </SnackbarContent>
    );
  }
);

export default ConvoV2Notification;
